import {
    AlertDialog,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@patient-watch/ui/alert-dialog";
import { createFileRoute } from "@tanstack/react-router";
import OnboardingEmailForm from "@/features/onboarding/components/OnboardingEmailForm";
import { OnboardingPatientPage } from "@/features/onboarding/components/OnboardingPatientPage";
import { useNavigateToNextOnboardingRoute } from "@/features/onboarding/hooks/useNavigateToNextOnboardingRoute";
import { useNavigateToPrevOnboardingRoute } from "@/features/onboarding/hooks/useNavigateToPrevOnboardingRoute";
import { persistOnboardingContactSkip } from "@/features/onboarding/utils/onboardingContactSkip";
import {
    invalidateProfileQuery,
    patchProfileQueryData,
    selectProfileByIdQueryOptions,
} from "@/features/profile/api/selectProfile";
import { useProfileCopy } from "@/features/i18n/profileCopy";
import { CancelButton, ContinueButton } from "@/features/i18n/formComponents";
import { useToastCopy } from "@/features/i18n/toastCopy";
import type { ProfileT } from "@patient-watch/types/database";
import { useAuth } from "@/utils/auth";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "@patient-watch/ui/sonner";

const Email = () => {
    const auth = useAuth();
    const { profile } = Route.useLoaderData();
    const copy = useProfileCopy();
    const toastCopy = useToastCopy();
    const queryClient = useQueryClient();
    const nextPage = useNavigateToNextOnboardingRoute("/onboarding/email");
    const backPage = useNavigateToPrevOnboardingRoute("/onboarding/email");
    const [isSkipping, setIsSkipping] = useState(false);
    const [skipOpen, setSkipOpen] = useState(false);

    const handleSkip = async () => {
        setIsSkipping(true);
        try {
            const userMetaData = await persistOnboardingContactSkip(auth.id, "email");
            patchProfileQueryData(queryClient, auth.id, { user_meta_data: userMetaData });
            void invalidateProfileQuery(queryClient, auth.id);
            nextPage();
        } catch (error) {
            toast.error(error instanceof Error ? error.message : toastCopy.couldNotSave);
            setIsSkipping(false);
        }
    };

    return (
        <OnboardingPatientPage step="email">
            <OnboardingEmailForm
                userId={auth.id}
                currentEmail={profile?.email ?? null}
                onSuccess={nextPage}
                onBack={backPage}
                tertiary={{
                    onClick: () => setSkipOpen(true),
                    disabled: isSkipping,
                    isLoading: isSkipping,
                }}
            />
            <AlertDialog open={skipOpen} onOpenChange={setSkipOpen}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>{copy.skipConfirmTitle}</AlertDialogTitle>
                        <AlertDialogDescription>{copy.skipEmailDescription}</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel asChild>
                            <CancelButton variant="outline" size="default" className="w-auto" />
                        </AlertDialogCancel>
                        <ContinueButton
                            size="default"
                            className="w-auto"
                            onClick={() => void handleSkip()}
                            disabled={isSkipping}
                            isLoading={isSkipping}
                            label={copy.continue}
                        />
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </OnboardingPatientPage>
    );
};

export const Route = createFileRoute("/onboarding/email")({
    component: Email,
    loader: async ({ context }) => {
        let profile: ProfileT | null = null;
        if (context?.auth?.id) {
            profile = await context?.queryClient.ensureQueryData(selectProfileByIdQueryOptions(context.auth.id));
        }
        return { profile };
    },
});
